import { apply, nodeMatrix } from '@repo/design-renderer';
import type { PointerEvent, ReactElement } from 'react';
import {
  type BoxNode,
  type FlatView,
  type HandleKind,
  handlePoints,
} from '@/features/design-editor';

interface SelectionOverlayProps {
  readonly node: BoxNode;
  readonly view: FlatView;
  readonly onPointerDownHandle: (kind: HandleKind, event: PointerEvent<SVGElement>) => void;
}

/** 컴포넌트 mm → 화면 px. 축마다 축척이 따로라 회전한 상자는 화면에서 평행사변형처럼 보일 수 있다(결과물과 같다). */
const toPx = (view: FlatView, xMm: number, yMm: number) => ({
  x: view.offsetX + xMm * view.pxPerMmX,
  y: view.offsetY + yMm * view.pxPerMmY,
});

/** 선택 테두리(회전 반영 — 모서리를 mm 에서 돌린 뒤 축마다 화면으로 옮긴다)와 핸들. 안내선 위 마지막 UI 층이다(ARC §6-4). */
export const SelectionOverlay = ({
  node,
  view,
  onPointerDownHandle,
}: SelectionOverlayProps): ReactElement => {
  const unflipped = node.type === 'image' ? { ...node, flipX: false, flipY: false } : node;
  const m = nodeMatrix(unflipped);
  const corners = [
    { x: 0, y: 0 },
    { x: node.width, y: 0 },
    { x: node.width, y: node.height },
    { x: 0, y: node.height },
  ].map((c) => {
    const p = apply(m, c);
    return toPx(view, p.x, p.y);
  });
  return (
    <svg className="pointer-events-none absolute inset-0 size-full" aria-hidden>
      <polygon
        points={corners.map((c) => `${c.x},${c.y}`).join(' ')}
        fill="none"
        stroke="currentColor"
        strokeWidth={1}
      />
      {handlePoints(node, { x: view.pxPerMmX, y: view.pxPerMmY }).map((handle) => {
        const p = toPx(view, handle.xMm, handle.yMm);
        return (
          <circle
            key={handle.kind}
            cx={p.x}
            cy={p.y}
            r={handle.kind === 'rotate' ? 6 : 5}
            className="pointer-events-auto fill-background stroke-current"
            data-handle={handle.kind}
            onPointerDown={(event) => onPointerDownHandle(handle.kind, event)}
          />
        );
      })}
    </svg>
  );
};
